import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CompanySettingsResponse,
  ItemResponse,
  ListResponse,
  NumberSequenceResponse,
  PublicBrandingResponse,
  UpdateCompanySettingsInput,
  UpdateNumberSequenceInput,
  UploadLetterheadInput,
  UploadLogoInput,
} from '@proerp/shared';
import { apiRequest } from '@/lib/apiClient';
import { readCachedBranding, writeCachedBranding } from '@/lib/brandingCache';

const SETTINGS_KEY = ['company-settings'] as const;
const BRANDING_KEY = ['public-branding'] as const;
const NUMBER_SEQUENCES_KEY = ['number-sequences'] as const;

export function useCompanySettings() {
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => apiRequest<ItemResponse<CompanySettingsResponse>>('/v1/company-settings'),
  });
}

/**
 * Öffentliches Branding (App-Logo, Login-Texte) — ohne Berechtigung abrufbar,
 * daher für alle Rollen im App-Kopf nutzbar (im Gegensatz zu `useCompanySettings`,
 * das `settings.read` verlangt). Lange gecacht; wird beim Logo-Upload invalidiert.
 *
 * `initialData` liest das zuletzt geladene Branding aus dem localStorage
 * (`brandingCache`) — sonst zeigen Topbar/Sidebar bis zur ersten Antwort das
 * Standard-Logo, obwohl längst ein eigenes hinterlegt ist. `initialDataUpdatedAt: 0`
 * markiert diesen Wert als sofort veraltet, damit trotzdem im Hintergrund neu
 * geladen wird (kein dauerhaft stehender Cache-Wert).
 */
export function usePublicBranding() {
  return useQuery({
    queryKey: BRANDING_KEY,
    queryFn: async () => {
      const response = await apiRequest<ItemResponse<PublicBrandingResponse>>('/v1/branding');
      writeCachedBranding(response.data);
      return response;
    },
    initialData: () => {
      const cached = readCachedBranding();
      return cached ? { data: cached } : undefined;
    },
    initialDataUpdatedAt: 0,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUploadLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UploadLogoInput) =>
      apiRequest<ItemResponse<CompanySettingsResponse>>('/v1/company-settings/logo', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SETTINGS_KEY }),
  });
}

export function useRemoveLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<ItemResponse<CompanySettingsResponse>>('/v1/company-settings/logo', {
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SETTINGS_KEY }),
  });
}

export function useUploadAppLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UploadLogoInput) =>
      apiRequest<ItemResponse<CompanySettingsResponse>>('/v1/company-settings/app-logo', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
      queryClient.invalidateQueries({ queryKey: BRANDING_KEY });
    },
  });
}

export function useRemoveAppLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<ItemResponse<CompanySettingsResponse>>('/v1/company-settings/app-logo', {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
      queryClient.invalidateQueries({ queryKey: BRANDING_KEY });
    },
  });
}

export function useUploadLetterhead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UploadLetterheadInput) =>
      apiRequest<ItemResponse<CompanySettingsResponse>>('/v1/company-settings/letterhead', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SETTINGS_KEY }),
  });
}

export function useRemoveLetterhead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<ItemResponse<CompanySettingsResponse>>('/v1/company-settings/letterhead', {
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SETTINGS_KEY }),
  });
}

export function useUpdateCompanySettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCompanySettingsInput) =>
      apiRequest<ItemResponse<CompanySettingsResponse>>('/v1/company-settings', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SETTINGS_KEY }),
  });
}

/**
 * Nummernkreise (ADR 0045) — Zählerstände von Belegen, Kommissionieraufträgen und
 * Projekten. Nur `settings.update` (kein eigenes Leserecht, siehe Controller).
 */
export function useNumberSequences() {
  return useQuery({
    queryKey: NUMBER_SEQUENCES_KEY,
    queryFn: () => apiRequest<ListResponse<NumberSequenceResponse>>('/v1/number-sequences'),
  });
}

export function useUpdateNumberSequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateNumberSequenceInput }) =>
      apiRequest<ItemResponse<NumberSequenceResponse>>(`/v1/number-sequences/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: NUMBER_SEQUENCES_KEY }),
  });
}
